import React from 'react'
import { Box, Button, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Link as RouterLink } from 'react-router-dom'
import ContentContainer from '../../areas/layout/content-container'
import MovieFilterIcon from '@mui/icons-material/MovieFilter'
import MovieIcon from '@mui/icons-material/Movie'
import TvIcon from '@mui/icons-material/Tv'
import PersonIcon from '@mui/icons-material/Person'
import CollectionsIcon from '@mui/icons-material/Collections'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

const Wrapper = styled(Box)(({ theme }) => ({
  minHeight: '60vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: theme.spacing(2),
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
}))

const BigCode = styled(Typography)(({ theme }) => ({
  fontSize: 72,
  fontWeight: 800,
  lineHeight: 1,
  color: theme.palette.brand.main,
  [theme.breakpoints.down('md')]: {
    fontSize: 56,
  },
}))

const ButtonRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
  flexWrap: 'wrap',
  justifyContent: 'center',
}))

const StatusIcon = styled(Box)(({ theme }) => ({
  color: theme.palette.brand.main,
  '& svg': { fontSize: 48 },
}))

const StatusTitle = styled(Typography)({
  fontSize: 20,
  fontWeight: 600,
})

const StatusDescription = styled(Typography)(({ theme }) => ({
  fontSize: 15,
  color: theme.palette.text.secondary,
  maxWidth: 480,
}))

const LinkButton = styled(Button)<{
  component?: React.ElementType
  to?: string
}>({})

export type StatusAction = {
  label: string
  to?: string
  onClick?: () => void
}

export type StatusPageProps = {
  code?: string
  icon?: React.ReactNode
  title?: string
  description?: string
  actions?: StatusAction[]
}

const StatusPage = ({
  code,
  icon = <MovieFilterIcon />,
  title = 'This page seems to be off the script.',
  description = "The page you're looking for doesn't exist or has moved.",
  actions = [{ label: 'Back to Home', to: '/' }],
}: StatusPageProps) => {
  return (
    <ContentContainer>
      <Wrapper>
        <StatusIcon>{icon}</StatusIcon>
        {code && <BigCode>{code}</BigCode>}
        <StatusTitle>{title}</StatusTitle>
        <StatusDescription>{description}</StatusDescription>
        <ButtonRow>
          {actions.map((action, i) => {
            const variant = i === 0 ? 'contained' : 'outlined'
            if (action.onClick) {
              return (
                <Button
                  key={action.label}
                  variant={variant}
                  onClick={action.onClick}
                >
                  {action.label}
                </Button>
              )
            }
            return (
              <LinkButton
                key={action.label}
                variant={variant}
                component={RouterLink}
                to={action.to ?? '/'}
              >
                {action.label}
              </LinkButton>
            )
          })}
        </ButtonRow>
      </Wrapper>
    </ContentContainer>
  )
}

export default StatusPage

export const RouteNotFound = () => (
  <StatusPage
    code='404'
    title='This page seems to be off the script.'
    description={
      "The page you're looking for doesn't exist or has moved. While you're here - did " +
      'you know Movieception can do a lot more than you might think?'
    }
    actions={[
      { label: 'Back to Home', to: '/' },
      { label: 'See what you can do', to: '/guide' },
    ]}
  />
)

export const MovieNotFound = (props?: Partial<StatusPageProps>) => (
  <StatusPage
    code='404'
    icon={<MovieIcon />}
    title="We couldn't find that movie."
    description="This movie doesn't exist or is no longer available."
    actions={[
      { label: 'Browse Movies', to: '/movies/popular' },
      { label: 'Back to Home', to: '/' },
    ]}
    {...props}
  />
)

export const ShowNotFound = (props?: Partial<StatusPageProps>) => (
  <StatusPage
    code='404'
    icon={<TvIcon />}
    title="We couldn't find that show."
    description="This show doesn't exist or is no longer available."
    actions={[
      { label: 'Browse Shows', to: '/shows/popular' },
      { label: 'Back to Home', to: '/' },
    ]}
    {...props}
  />
)

export const SeasonNotFound = (props?: Partial<StatusPageProps>) => (
  <StatusPage
    code='404'
    icon={<TvIcon />}
    title="That season isn't available."
    description="We couldn't find that season. It may not exist yet."
    actions={[{ label: 'Back to Home', to: '/' }]}
    {...props}
  />
)

export const ActorNotFound = (props?: Partial<StatusPageProps>) => (
  <StatusPage
    code='404'
    icon={<PersonIcon />}
    title="We couldn't find that person."
    description="This person doesn't exist or is no longer available."
    actions={[
      { label: 'Popular Actors', to: '/actors/popular' },
      { label: 'Back to Home', to: '/' },
    ]}
    {...props}
  />
)

export const CollectionNotFound = (props?: Partial<StatusPageProps>) => (
  <StatusPage
    code='404'
    icon={<CollectionsIcon />}
    title="That collection doesn't exist."
    description="We couldn't find that collection. It may have been removed."
    actions={[
      { label: 'Discover', to: '/discover' },
      { label: 'Back to Home', to: '/' },
    ]}
    {...props}
  />
)

export const UserNotFound = (props?: Partial<StatusPageProps>) => (
  <StatusPage
    code='404'
    icon={<PersonIcon />}
    title="This profile isn't available."
    description="This profile doesn't exist or is private."
    actions={[{ label: 'Back to Home', to: '/' }]}
    {...props}
  />
)

// For 500s / network failures. No code, and a "Try again" button wired to the
// caller's retry via onRetry, plus a home escape hatch.
export const LoadError = ({
  onRetry,
  ...props
}: Partial<StatusPageProps> & { onRetry?: () => void }) => (
  <StatusPage
    icon={<ErrorOutlineIcon />}
    title='Something went wrong loading this.'
    description='This is on us, not you. Give it another try in a moment.'
    actions={[
      ...(onRetry ? [{ label: 'Try again', onClick: onRetry }] : []),
      { label: 'Back to Home', to: '/' },
    ]}
    {...props}
  />
)
